import { useEffect, useMemo, useState } from 'react';
import {
  X,
  Trash2,
  Plus,
  ChevronDown,
  ChevronUp,
  Check,
} from 'lucide-react';
import type {
  CategoriaVista,
  PaqueteCrearInput,
  PaqueteDetalle,
  ProductoVista,
} from '@shared/types';
import { NumberField } from '../ui/NumberField';
import {
  VentanaDisponibilidadFields,
  type ValorVentana,
} from '@renderer/components/forms/VentanaDisponibilidadFields';
import {
  calcularPreviewPaquete,
  type RenglonPreviewInput,
} from '@shared/paquetePreview';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';
import { pesosACentavos, centavosAPesos } from '@shared/centavos';

interface Props {
  abierto: boolean;
  paqueteId: number | null;
  categorias: CategoriaVista[];
  onClose: () => void;
  onSaved: () => void;
}

interface RenglonRow {
  productoId: number | null;
  /** Sprint 14: entero ≥ 1 ("cuántas unidades del producto van en el paquete"). */
  cantidad: number;
  esObligatorio: boolean;
  /** Sprint 44. Si true, el cliente puede pedir "sin esto" en POS y el precio baja. */
  excluible: boolean;
  /** Sprint 73 — Va de regalo dentro del paquete: sin costo, pero descuenta inventario. */
  esRegalo: boolean;
  sustitutosIds: number[];
}

/**
 * Sprint 9.4 — FormPaquete refactor.
 *
 * Cambios vs Sprint 7.4:
 *   - Renglones colapsables (acordeón): por defecto solo el último abierto,
 *     los demás muestran resumen 1-línea. Reduce scroll de ~1000px a <600px
 *     con 5 renglones.
 *   - Sustitutos como CHIPS visibles + dropdown para agregar/quitar.
 *     Antes había que abrir un menú para ver qué se eligió.
 *   - Panel lateral derecho con `PreviewPanel`: muestra precio base, Δ máximo
 *     y "precio máximo con sustituciones" calculado con `calcularPreviewPaquete`.
 *
 * Layout: 2 columnas en md+ (form izquierda, preview derecha), 1 columna en sm.
 */
export function FormPaquete({
  abierto,
  paqueteId,
  categorias,
  onClose,
  onSaved,
}: Props) {
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [precioBase, setPrecioBase] = useState(0);
  const [categoriaId, setCategoriaId] = useState<number | undefined>();
  const [habilitado, setHabilitado] = useState(true);
  /*
    Sprint 74 — Ventana de disponibilidad del paquete.

    Todo en null = disponible siempre, que es cómo se han comportado todos los paquetes hasta ahora:
    abrir este formulario y guardar sin tocar nada no cambia nada.
  */
  const [ventana, setVentana] = useState<ValorVentana>({
    diasSemana: null,
    horaInicio: null,
    horaFin: null,
  });
  const [renglones, setRenglones] = useState<RenglonRow[]>([]);
  const [productos, setProductos] = useState<ProductoVista[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  /** idx del renglón actualmente expandido. -1 si todos colapsados. */
  const [expandido, setExpandido] = useState<number>(-1);

  useEffect(() => {
    if (!abierto) return;
    setError(null);

    /**
     * Sprint 79 — También los productos ocultos, marcados como tales.
     *
     * Antes se filtraban (`filter(p => p.habilitado)`) y eso dejaba a la dueña encerrada: ocultó
     * las bebidas del menú, sus cinco paquetes las llevaban dentro, y al abrir un paquete para
     * **quitarles la bebida** el renglón ni siquiera aparecía en la lista. No podía arreglar lo
     * que el sistema le exigía arreglar.
     *
     * Que estén ocultos se ve en el nombre; lo que un paquete puede vender hoy ya lo dice el POS
     * con su motivo ("falta Coca de 3 L"). Esconderlos aquí no protegía nada.
     */
    window.api.productos.listar().then((res) => {
      if (res.ok) {
        setProductos(
          res.data.map((p) =>
            p.habilitado ? p : { ...p, nombre: `${p.nombre} (oculto)` },
          ),
        );
      }
    });

    if (paqueteId) {
      window.api.paquetes.obtener(paqueteId).then((res) => {
        if (res.ok && res.data) {
          const d: PaqueteDetalle = res.data;
          setNombre(d.nombre);
          setDescripcion(d.descripcion ?? '');
          // Sprint 39 — state en centavos (igual que backend).
          setPrecioBase(d.precioBase);
          setCategoriaId(d.categoriaId ?? undefined);
          setHabilitado(d.habilitado);
          setVentana({
            diasSemana: d.diasSemana ?? null,
            horaInicio: d.horaInicio ?? null,
            horaFin: d.horaFin ?? null,
          });
          const rs = d.productos
            .slice()
            .sort((a, b) => a.posicion - b.posicion)
            .map((r) => ({
              productoId: r.productoId,
              cantidad: r.cantidad,
              esObligatorio: r.esObligatorio,
              excluible: r.excluible,
              esRegalo: r.esRegalo ?? false,
              sustitutosIds: r.sustitutos.map((s) => s.productoId),
            }));
          setRenglones(rs);
          // Editar: arrancar todos colapsados para mostrar el panorama.
          setExpandido(-1);
        }
      });
    } else {
      setNombre('');
      setDescripcion('');
      setPrecioBase(0);
      setCategoriaId(undefined);
      setHabilitado(true);
      setRenglones([]);
      setExpandido(-1);
    }
  }, [abierto, paqueteId]);

  const productosPorId = useMemo(() => {
    const m = new Map<number, ProductoVista>();
    productos.forEach((p) => m.set(p.id, p));
    return m;
  }, [productos]);

  const preview = useMemo(() => {
    const inputs: RenglonPreviewInput[] = renglones.map((r) => ({
      productoId: r.productoId,
      cantidad: r.cantidad,
      sustitutosIds: r.sustitutosIds,
    }));
    return calcularPreviewPaquete(precioBase, inputs, productosPorId);
  }, [renglones, precioBase, productosPorId]);

  if (!abierto) return null;

  const agregarRenglon = () => {
    setRenglones((prev) => {
      const next = [
        ...prev,
        {
          productoId: null,
          cantidad: 1,
          esObligatorio: true,
          excluible: false,
          esRegalo: false,
          sustitutosIds: [] as number[],
        },
      ];
      // Auto-expandir el recién agregado.
      setExpandido(next.length - 1);
      return next;
    });
  };

  const actualizarRenglon = (idx: number, cambios: Partial<RenglonRow>) => {
    setRenglones((prev) =>
      prev.map((r, i) => {
        if (i !== idx) return r;
        const next = { ...r, ...cambios };
        if (cambios.productoId !== undefined && cambios.productoId !== null) {
          next.sustitutosIds = next.sustitutosIds.filter(
            (sid) => sid !== cambios.productoId,
          );
        }
        return next;
      }),
    );
  };

  const quitarRenglon = (idx: number) => {
    setRenglones((prev) => prev.filter((_, i) => i !== idx));
    setExpandido((cur) => (cur === idx ? -1 : cur > idx ? cur - 1 : cur));
  };

  const toggleExpand = (idx: number) => {
    setExpandido((cur) => (cur === idx ? -1 : idx));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (nombre.trim().length < 1) {
      setError('El nombre es obligatorio');
      return;
    }
    if (precioBase <= 0) {
      setError('El precio base debe ser mayor a 0');
      return;
    }
    if (renglones.length < 2) {
      setError('Un paquete debe tener al menos 2 productos.');
      return;
    }
    const sinProducto = renglones.findIndex((r) => !r.productoId);
    if (sinProducto >= 0) {
      setError(`Renglón #${sinProducto + 1}: selecciona un producto.`);
      setExpandido(sinProducto);
      return;
    }
    const cantidadInvalida = renglones.findIndex(
      (r) => !Number.isInteger(r.cantidad) || r.cantidad < 1,
    );
    if (cantidadInvalida >= 0) {
      setError(`Renglón #${cantidadInvalida + 1}: cuántos debe ser un entero ≥ 1.`);
      setExpandido(cantidadInvalida);
      return;
    }
    for (let i = 0; i < renglones.length; i++) {
      const r = renglones[i];
      for (const sid of r.sustitutosIds) {
        if (sid === r.productoId) {
          setError(`Renglón #${i + 1}: un producto no puede ser su propio sustituto.`);
          setExpandido(i);
          return;
        }
      }
      const dup = new Set<number>();
      for (const sid of r.sustitutosIds) {
        if (dup.has(sid)) {
          setError(`Renglón #${i + 1}: hay sustitutos duplicados.`);
          setExpandido(i);
          return;
        }
        dup.add(sid);
      }
    }

    setEnviando(true);
    // Sprint 39 — state ya en centavos; backend espera centavos.
    const data: PaqueteCrearInput = {
      nombre: nombre.trim(),
      descripcion: descripcion || undefined,
      precioBase,
      categoriaId,
      habilitado,
      // Sprint 74 — `null` explícito: quitar el horario tiene que borrar el guardado.
      diasSemana: ventana.diasSemana,
      horaInicio: ventana.horaInicio,
      horaFin: ventana.horaFin,
      productos: renglones.map((r, idx) => ({
        productoId: r.productoId!,
        cantidad: r.cantidad,
        posicion: idx + 1,
        esObligatorio: r.esObligatorio,
        excluible: r.excluible,
        esRegalo: r.esRegalo,
        sustitutosIds: r.sustitutosIds,
      })),
    };
    const res = paqueteId
      ? await window.api.paquetes.actualizar(paqueteId, data)
      : await window.api.paquetes.crear(data);
    setEnviando(false);
    if (res.ok) onSaved();
    else setError(traducirErrorBackend(res.error, res.codigo));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl rounded-xl bg-white shadow-xl max-h-[92vh] overflow-hidden flex flex-col md:max-w-4xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 bg-white shrink-0">
          <h2 className="text-lg font-semibold">
            {paqueteId ? 'Editar paquete' : 'Nuevo paquete'}
          </h2>
          <button onClick={onClose} className="btn-ghost p-2" aria-label="Cerrar">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="flex-1 overflow-auto">
          <div className="grid grid-cols-1 gap-4 px-6 py-5 lg:grid-cols-3 lg:gap-6">
            {/* Columna izquierda: formulario principal (2/3) */}
            <div className="md:col-span-2 space-y-5">
              <div>
                <label className="label" htmlFor="paqNombre">Nombre *</label>
                <input
                  id="paqNombre"
                  className="input"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  placeholder="Espadas Cerdo Mix"
                  autoFocus
                />
              </div>

              <div>
                <label className="label" htmlFor="paqDesc">Descripción</label>
                <textarea
                  id="paqDesc"
                  className="input"
                  rows={2}
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label" htmlFor="paqPrecio">Precio base *</label>
                  {/* Sprint 39 — UX en pesos, state en centavos. */}
                  <NumberField
                    id="paqPrecio"
                    min={0}
                    decimals={2}
                    className="input"
                    value={centavosAPesos(precioBase)}
                    onChange={(n) => setPrecioBase(pesosACentavos(n))}
                  />
                </div>
                <div>
                  <label className="label" htmlFor="paqCat">Categoría</label>
                  <select
                    id="paqCat"
                    className="input"
                    value={categoriaId ?? ''}
                    onChange={(e) =>
                      setCategoriaId(e.target.value ? Number(e.target.value) : undefined)
                    }
                  >
                    <option value="">Sin categoría</option>
                    {categorias.map((c) => (
                      <option key={c.id} value={c.id}>{c.nombre}</option>
                    ))}
                  </select>
            {/*
              Sprint 73 — "Sin categoría" no es inocuo.

              Reporte del negocio: *"en los paquetes está la opción CATEGORÍA… ¿qué pongo?"*. Un
              paquete sin categoría **sólo aparece en la pestaña "Todas"** del punto de venta: en
              cuanto el operador elige una categoría, desaparece. Eso hay que decirlo aquí, que es
              donde se decide, y no descubrirlo en hora pico buscando un paquete que no sale.
            */}
            {!categoriaId && (
              <p className="mt-1 rounded bg-amber-50 px-2 py-1 text-xs text-amber-900">
                Sin categoría, este paquete sólo se verá en la pestaña <b>Todas</b> del punto de
                venta. Ponle una —por ejemplo <b>PAQUETES</b>— para que se encuentre rápido.
              </p>
            )}
                </div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={habilitado}
                  onChange={(e) => setHabilitado(e.target.checked)}
                  className="h-4 w-4"
                />
                <span className="text-sm">Habilitado para venta</span>
              </label>

              {/*
                Sprint 74 — Horario del paquete.

                *Promo Amigos* ($599) y *Gratis medio pollo* ($299) son paquetes con precio de
                promoción y horario. Sin esto había que prenderlos y apagarlos a mano dos veces al
                día — y el día que se olvide, el paquete de $599 se vende a las nueve de la noche.
              */}
              <VentanaDisponibilidadFields
                queEs="este paquete"
                valor={ventana}
                onChange={setVentana}
              />

              <ProductosEditor
                renglones={renglones}
                productos={productos}
                productosPorId={productosPorId}
                expandido={expandido}
                onToggleExpand={toggleExpand}
                onActualizar={actualizarRenglon}
                onAgregar={agregarRenglon}
                onQuitar={quitarRenglon}
              />
            </div>

            {/* Columna derecha: preview lateral (1/3) */}
            <div className="md:col-span-1">
              <PreviewPanel
                precioBase={precioBase}
                preview={preview}
                renglonesCount={renglones.length}
              />
            </div>
          </div>

          {error && (
            <div className="mx-6 mb-3">
              <div className="error-text">{error}</div>
            </div>
          )}

          <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200 bg-white">
            <button type="button" onClick={onClose} className="btn-ghost" disabled={enviando}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={enviando}>
              {paqueteId ? 'Guardar cambios' : 'Crear paquete'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Panel lateral: muestra precio base, Δ máximo con sustituciones y total
 * máximo. Si no hay renglones, sólo informa que se mostrará al agregar.
 */
function PreviewPanel({
  precioBase,
  preview,
  renglonesCount,
}: {
  precioBase: number;
  preview: ReturnType<typeof calcularPreviewPaquete>;
  renglonesCount: number;
}) {
  // Sprint 39 — recibe centavos, formatea como pesos.
  const fmt = (centavos: number) => (centavos / 100).toFixed(2);

  return (
    <div className="md:sticky md:top-0 rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
      <h3 className="font-semibold text-sm uppercase tracking-wide text-slate-600">
        Resumen de precios
      </h3>

      <div className="space-y-1.5 text-sm">
        <div className="flex justify-between">
          <span className="text-slate-600">Precio base:</span>
          <span className="font-mono font-semibold">${fmt(precioBase)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-600">Δ por sustituciones:</span>
          <span
            className={`font-mono font-semibold ${
              preview.deltaMaximoTotal > 0
                ? 'text-amber-700'
                : preview.deltaMaximoTotal < 0
                ? 'text-green-700'
                : 'text-slate-400'
            }`}
          >
            {preview.deltaMaximoTotal >= 0
              ? `+$${fmt(preview.deltaMaximoTotal)}`
              : `-$${fmt(Math.abs(preview.deltaMaximoTotal))}`}
          </span>
        </div>
        <div className="flex justify-between border-t border-slate-300 pt-1.5 mt-1.5">
          <span className="font-semibold">
            {preview.deltaMaximoTotal < 0 ? 'Precio mín:' : 'Precio máx:'}
          </span>
          <span className="font-mono font-bold text-primary text-base">
            ${fmt(preview.precioMaximo)}
          </span>
        </div>
      </div>

      <div className="text-xs text-slate-500 leading-relaxed">
        Sustitutos más caros generan un cargo (+); sustitutos más baratos aplican un descuento (−).
        Si elige los defaults, paga el precio base.
      </div>

      {renglonesCount === 0 ? (
        <div className="text-xs text-slate-400 italic pt-2 border-t border-slate-200">
          Agrega productos al paquete para ver el desglose.
        </div>
      ) : preview.renglonesConSustitutos === 0 ? (
        <div className="text-xs text-slate-500 pt-2 border-t border-slate-200">
          Ningún renglón tiene sustitutos — el precio será fijo en ${fmt(precioBase)}.
        </div>
      ) : (
        <details className="text-xs text-slate-600 pt-2 border-t border-slate-200">
          <summary className="cursor-pointer font-medium">
            Ver desglose por renglón ({preview.renglonesConSustitutos})
          </summary>
          <ul className="mt-2 space-y-1.5">
            {preview.renglones
              .filter((r) => r.deltaMaximoRenglon !== 0)
              .map((r) => (
                <li key={r.posicion} className="leading-tight">
                  <div className="font-medium">#{r.posicion} {r.baseNombre}</div>
                  <div className={`${r.deltaMaximoRenglon < 0 ? 'text-green-700' : 'text-slate-500'}`}>
                    Δ {r.deltaMaximoRenglon >= 0 ? `+$${fmt(r.deltaMaximoRenglon)}` : `-$${fmt(Math.abs(r.deltaMaximoRenglon))}`} ·{' '}
                    {r.sustitutos.length} sustituto
                    {r.sustitutos.length === 1 ? '' : 's'}
                  </div>
                </li>
              ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function ProductosEditor({
  renglones,
  productos,
  productosPorId,
  expandido,
  onToggleExpand,
  onActualizar,
  onAgregar,
  onQuitar,
}: {
  renglones: RenglonRow[];
  productos: ProductoVista[];
  productosPorId: Map<number, ProductoVista>;
  expandido: number;
  onToggleExpand: (idx: number) => void;
  onActualizar: (idx: number, cambios: Partial<RenglonRow>) => void;
  onAgregar: () => void;
  onQuitar: (idx: number) => void;
}) {
  return (
    <div className="border-t border-slate-200 pt-4">
      <div className="mb-3">
        <h3 className="font-semibold">Productos del paquete</h3>
        <p className="text-xs text-slate-500">
          Mínimo 2. Toca un renglón para expandirlo y editar sus sustitutos.
          El precio se ajusta automáticamente cuando el cliente sustituye.
        </p>
      </div>

      <div className="space-y-2">
        {renglones.map((row, idx) => (
          <RenglonAccordion
            key={idx}
            row={row}
            idx={idx}
            expandido={expandido === idx}
            productos={productos}
            productosPorId={productosPorId}
            renglones={renglones}
            onToggleExpand={() => onToggleExpand(idx)}
            onActualizar={onActualizar}
            onQuitar={onQuitar}
          />
        ))}

        <button type="button" onClick={onAgregar} className="btn-secondary w-full">
          <Plus className="h-4 w-4" />
          Agregar producto al paquete
        </button>
      </div>
    </div>
  );
}

/**
 * Renglón colapsable: cabecera siempre visible, body solo si `expandido`.
 * Cabecera muestra: posición, nombre del producto base, cantidad/unidad y
 * conteo de sustitutos. Es navegable de un vistazo aunque haya 8 renglones.
 */
function RenglonAccordion({
  row,
  idx,
  expandido,
  productos,
  productosPorId,
  renglones,
  onToggleExpand,
  onActualizar,
  onQuitar,
}: {
  row: RenglonRow;
  idx: number;
  expandido: boolean;
  productos: ProductoVista[];
  productosPorId: Map<number, ProductoVista>;
  renglones: RenglonRow[];
  onToggleExpand: () => void;
  onActualizar: (idx: number, cambios: Partial<RenglonRow>) => void;
  onQuitar: (idx: number) => void;
}) {
  const productoBase = row.productoId ? productosPorId.get(row.productoId) ?? null : null;

  const idsBaseEnPaquete = new Set(
    renglones
      .map((r, i) => (i !== idx ? r.productoId : null))
      .filter((v): v is number => v !== null),
  );
  const sustitutosElegibles = productos.filter(
    (p) => p.id !== row.productoId && !idsBaseEnPaquete.has(p.id),
  );

  const headerLabel = productoBase
    ? `${productoBase.nombre} · ×${row.cantidad}`
    : 'Sin producto seleccionado';

  const haySustitutos = row.sustitutosIds.length > 0;

  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      {/* Cabecera siempre visible */}
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={onToggleExpand}
          className="flex flex-1 items-center gap-2 text-left"
          aria-expanded={expandido}
        >
          <span className="rounded-full bg-primary text-white text-xs font-bold w-6 h-6 flex items-center justify-center shrink-0">
            {idx + 1}
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm truncate">{headerLabel}</div>
            <div className="text-xs text-slate-500">
              {/*
                Sprint 73 — El regalo se anuncia primero y en verde.

                Es lo que la dueña quiere ver de un vistazo: *"en las espadas van gratis los frijoles
                charros"*. Y evita la duda de si ese renglón le está sumando al precio del paquete.
              */}
              {row.esRegalo && <span className="font-semibold text-emerald-700">GRATIS · </span>}
              {haySustitutos
                ? `${row.sustitutosIds.length} sustituto${row.sustitutosIds.length === 1 ? '' : 's'}`
                : 'fijo · sin sustitutos'}
              {row.excluible && !row.esRegalo ? ' · el cliente puede pedirlo sin' : ''}
            </div>
          </div>
          {expandido ? (
            <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" />
          ) : (
            <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
          )}
        </button>
        <button
          type="button"
          onClick={() => onQuitar(idx)}
          className="text-red-600 hover:bg-red-50 p-1.5 rounded shrink-0"
          aria-label="Quitar renglón"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {/* Body expandible */}
      {expandido && (
        <div className="border-t border-slate-100 px-3 py-3 space-y-3 bg-slate-50/40">
          <div>
            <label className="text-slate-500 block mb-1 text-xs">Producto base</label>
            <select
              className="input text-sm"
              value={row.productoId ?? ''}
              onChange={(e) =>
                onActualizar(idx, { productoId: e.target.value ? Number(e.target.value) : null })
              }
            >
              <option value="">— Selecciona producto base —</option>
              {productos.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                  {p.precioEnPaquete != null ? ` · ${(p.precioEnPaquete / 100).toFixed(2)} en paq` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs items-end">
            <div className="md:col-span-1">
              <label className="text-slate-500 block mb-1">Cuántos</label>
              <NumberField
                min={1}
                decimals={0}
                className="input text-sm"
                value={row.cantidad}
                onChange={(n) => onActualizar(idx, { cantidad: n })}
              />
              <div className="text-[11px] text-slate-400 mt-1">
                {productoBase
                  ? `Unidades de "${productoBase.nombre}" que van en el paquete.`
                  : 'Cantidad de unidades del producto.'}
              </div>
            </div>
            {/*
              Sprint 73 — "Va de regalo".

              Pregunta de la dueña: *"en las espadas va gratis los frijoles charros 1/2, ¿cómo puedo
              agregarlo para que me aparezca al momento de incluirlo sin que aparezca el costo?"*. El
              precio del paquete es fijo, así que ningún renglón le suma — esta casilla es la que hace
              que se **vea** gratis y la que activa la salida para cuando el regalo se acabe.
            */}
            <div className="md:col-span-3">
              <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-sm">
                <input
                  type="checkbox"
                  checked={row.esRegalo}
                  onChange={(e) =>
                    onActualizar(idx, {
                      esRegalo: e.target.checked,
                      // Un regalo tiene que poder quitarse si se acaba: es la premisa de la dueña de
                      // "descontar su precio para tener un cliente feliz".
                      excluible: e.target.checked ? true : row.excluible,
                    })
                  }
                  className="mt-0.5 h-4 w-4"
                />
                <span>
                  <strong>Va de regalo</strong> (sin costo)
                  <span className="block text-[11px] text-emerald-800">
                    Se entrega incluido en el paquete y no se cobra aparte. <b>Sí se descuenta del
                    inventario</b>, para que sepas cuánto se está regalando. Si se acaba y no le pones
                    sustituto, se puede quitar y el paquete baja su precio de lista.
                  </span>
                </span>
              </label>
            </div>
            <div className="md:col-span-2">
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={row.excluible}
                  disabled={row.esRegalo}
                  onChange={(e) => onActualizar(idx, { excluible: e.target.checked })}
                  className="h-4 w-4 mt-0.5"
                />
                <span>
                  El cliente puede pedirlo <strong>sin esto</strong>
                  <span className="block text-[11px] text-slate-400">
                    Sprint 44. Al excluir, el paquete baja en el precio de este
                    renglón y no se descuenta stock.
                  </span>
                </span>
              </label>
            </div>
          </div>

          <div>
            <label className="text-slate-500 block mb-1 text-xs">
              Sustitutos válidos ({row.sustitutosIds.length})
            </label>
            <SustitutosChips
              elegibles={sustitutosElegibles}
              productosPorId={productosPorId}
              seleccionadosIds={row.sustitutosIds}
              onChange={(ids) => onActualizar(idx, { sustitutosIds: ids })}
              deshabilitado={!productoBase}
              baseProducto={productoBase}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Sustitutos como chips visibles + dropdown para agregar más.
 * Mejora vs Sprint 7.4: la dueña ve qué eligió sin tener que abrir un menú.
 */
function SustitutosChips({
  elegibles,
  productosPorId,
  seleccionadosIds,
  onChange,
  deshabilitado,
  baseProducto,
}: {
  elegibles: ProductoVista[];
  productosPorId: Map<number, ProductoVista>;
  seleccionadosIds: number[];
  onChange: (ids: number[]) => void;
  deshabilitado: boolean;
  baseProducto: ProductoVista | null;
}) {
  const [abierto, setAbierto] = useState(false);
  const noElegidos = elegibles.filter((p) => !seleccionadosIds.includes(p.id));

  const quitarChip = (id: number) => {
    onChange(seleccionadosIds.filter((x) => x !== id));
  };

  const agregar = (id: number) => {
    onChange([...seleccionadosIds, id]);
  };

  const basePrecio = baseProducto
    ? baseProducto.precioEnPaquete ?? baseProducto.precio
    : 0;

  return (
    <div className="space-y-2">
      {/* Chips de seleccionados */}
      {seleccionadosIds.length > 0 ? (
        <>
          <div className="flex flex-wrap gap-1.5">
            {seleccionadosIds.map((sid) => {
              const p = productosPorId.get(sid);
              if (!p) return null;
              const precio = p.precioEnPaquete ?? p.precio;
              const delta = baseProducto ? precio - basePrecio : 0;
              const crossUnidad =
                !!baseProducto && p.unidadVenta !== baseProducto.unidadVenta;
              return (
                <span
                  key={sid}
                  className={`inline-flex items-center gap-1 rounded-full text-xs font-medium pl-2.5 pr-1 py-1 ${
                    crossUnidad
                      ? 'bg-amber-100 text-amber-900 border border-amber-200'
                      : 'bg-primary/10 text-primary'
                  }`}
                  title={crossUnidad ? `Cross-unidad: ${p.unidadVenta} ↔ ${baseProducto?.unidadVenta}` : undefined}
                >
                  <span>{p.nombre}</span>
                  {crossUnidad && <span className="text-xs uppercase">⇆</span>}
                  {delta !== 0 && (
                    <span className={`font-mono ${delta < 0 ? 'text-green-700' : 'text-amber-700'}`}>
                      {delta >= 0 ? `+$${(delta / 100).toFixed(2)}` : `-$${(Math.abs(delta) / 100).toFixed(2)}`}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => quitarChip(sid)}
                    className={`rounded-full p-0.5 ${crossUnidad ? 'hover:bg-amber-200' : 'hover:bg-primary/20'}`}
                    aria-label={`Quitar ${p.nombre}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              );
            })}
          </div>
          {seleccionadosIds.some((sid) => {
            const p = productosPorId.get(sid);
            return p && baseProducto && p.unidadVenta !== baseProducto.unidadVenta;
          }) && (
            <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1">
              ⇆ Hay sustitutos de unidad distinta a "{baseProducto?.unidadVenta}".
              Al elegirlos, el sistema cobra el precio del sustituto y descuenta
              stock por su propia unidad (no por la cantidad del renglón).
            </div>
          )}
        </>
      ) : (
        <div className="text-xs text-slate-400 italic">
          {deshabilitado
            ? 'Selecciona primero el producto base'
            : 'Sin sustitutos — el cliente no puede cambiar este renglón.'}
        </div>
      )}

      {/* Panel inline para agregar — NO usa absolute/relative para evitar
          ser recortado por el overflow-hidden del RenglonAccordion padre. */}
      {!deshabilitado && (
        <div>
          <button
            type="button"
            onClick={() => setAbierto((v) => !v)}
            className="btn-ghost text-xs"
          >
            <Plus className="h-3 w-3" />
            {abierto ? 'Ocultar lista' : 'Agregar sustituto'}
          </button>
          {abierto && (
            <div className="mt-2 rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="max-h-52 overflow-y-auto">
                {noElegidos.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-slate-500">
                    No quedan productos elegibles.
                  </div>
                ) : (
                  noElegidos.map((p) => {
                    const precio = p.precioEnPaquete ?? p.precio;
                    const delta = precio - basePrecio;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => agregar(p.id)}
                        className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-slate-50 border-b border-slate-100 last:border-0"
                      >
                        <Check className="h-3 w-3 text-transparent shrink-0" />
                        <span className="flex-1">{p.nombre}</span>
                        <span className="text-xs text-slate-500 shrink-0 font-mono">
                          ${(precio / 100).toFixed(2)}
                          {delta !== 0 && (
                            <span className={`ml-1 ${delta < 0 ? 'text-green-700' : 'text-amber-700'}`}>
                              {delta >= 0 ? `+$${(delta / 100).toFixed(2)}` : `-$${(Math.abs(delta) / 100).toFixed(2)}`}
                            </span>
                          )}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
              <div className="border-t border-slate-200 px-3 py-1.5 flex justify-end bg-slate-50">
                <button
                  type="button"
                  onClick={() => setAbierto(false)}
                  className="text-xs text-primary font-medium"
                >
                  Cerrar
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
